import type { PointState } from "./generated/widget-fixture";

// The small icon in front of a widget's label (VisionPlatform device-dashboards DDB-35). Profiles carry no icon yet,
// so it is chosen from the unit, then from the point's name; a reading that matches nothing has no icon.
type Kind = "temperature" | "humidity" | "battery" | "signal" | "power" | "wind" | "light" | "pressure";

const BY_UNIT: Record<string, Kind> = {
  "°C": "temperature",
  "°F": "temperature",
  K: "temperature",
  "%RH": "humidity",
  dBm: "signal",
  dB: "signal",
  V: "power",
  mV: "power",
  A: "power",
  mA: "power",
  W: "power",
  kW: "power",
  kWh: "power",
  "m/s": "wind",
  "km/h": "wind",
  lx: "light",
  bar: "pressure",
  mbar: "pressure",
  Pa: "pressure",
  hPa: "pressure",
  kPa: "pressure",
};
const BY_NAME: [RegExp, Kind][] = [
  [/batt|baterie/i, "battery"],
  [/humid|umidit/i, "humidity"],
  [/temp/i, "temperature"],
  [/signal|semnal|rssi/i, "signal"],
];

const PATHS: Record<Kind, string> = {
  temperature: "M10 4a2 2 0 1 1 4 0v9.5a4 4 0 1 1-4 0z",
  humidity: "M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z",
  battery: "M4 8h14v8H4zM18 11h2v2h-2",
  signal: "M5 19v-3M10 19v-7M15 19v-11M20 19V4",
  power: "M13 3 5 14h6l-1 7 8-11h-6z",
  wind: "M3 9h11a3 3 0 1 0-3-3M3 15h15a3 3 0 1 1-3 3",
  light: "M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM12 2v2M12 20v2M2 12h2M20 12h2",
  pressure: "M4 16a8 8 0 1 1 16 0M12 16l4-5",
};
const TINT: Record<Kind, string> = {
  temperature: "bg-orange-50 text-orange-700",
  humidity: "bg-sky-50 text-sky-700",
  battery: "bg-emerald-50 text-emerald-700",
  signal: "bg-indigo-50 text-indigo-700",
  power: "bg-yellow-50 text-yellow-700",
  wind: "bg-cyan-50 text-cyan-700",
  light: "bg-amber-50 text-amber-700",
  pressure: "bg-violet-50 text-violet-700",
};

export function iconKind(point: Pick<PointState, "unit" | "name"> | undefined): Kind | undefined {
  if (!point) return undefined;
  return (point.unit ? BY_UNIT[point.unit] : undefined) ?? BY_NAME.find(([re]) => re.test(point.name))?.[1];
}

export function PointIcon({ point }: { point: Pick<PointState, "unit" | "name"> | undefined }) {
  const kind = iconKind(point);
  if (!kind) return null;
  return (
    <span
      data-icon={kind}
      aria-hidden="true"
      className={`grid size-6 shrink-0 place-items-center rounded-md ${TINT[kind]}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={PATHS[kind]} />
      </svg>
    </span>
  );
}
